// ModalManagerContext.tsx
import React, { createContext, useState, useContext, ReactNode } from 'react';

interface ModalManagerContextType {
  activeModals: Set<string>;
  registerModal: (id: string, closeHandler: () => void) => void;
  unregisterModal: (id: string) => void;
  closeAllExcept: (exceptId?: string) => void;
  isModalActive: (id: string) => boolean;
  openModal: (id: string) => void;
  closeModal: (id: string) => void;
}

const ModalManagerContext = createContext<ModalManagerContextType>({
  activeModals: new Set(),
  registerModal: () => {},
  unregisterModal: () => {},
  closeAllExcept: () => {},
  isModalActive: () => false,
  openModal: () => {},
  closeModal: () => {},
});

export const ModalManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeModals, setActiveModals] = useState<Set<string>>(new Set());
  const [registeredModals, setRegisteredModals] = useState<Map<string, () => void>>(new Map());

  const registerModal = (id: string, closeHandler: () => void) => {
    setRegisteredModals(prev => {
      const newMap = new Map(prev);
      newMap.set(id, closeHandler);
      return newMap;
    });
  };

  const unregisterModal = (id: string) => {
    setRegisteredModals(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
    closeModal(id);
  };

  const openModal = (id: string) => {
    setActiveModals(prev => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  };

  const closeModal = (id: string) => {
    setActiveModals(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const closeAllExcept = (exceptId?: string) => {
    registeredModals.forEach((closeHandler, id) => {
      if (id !== exceptId) {
        closeHandler();
      }
    });
  };

  const isModalActive = (id: string) => {
    return activeModals.has(id);
  };

  return (
    <ModalManagerContext.Provider value={{
      activeModals,
      registerModal,
      unregisterModal,
      closeAllExcept,
      isModalActive,
      openModal,
      closeModal,
    }}>
      {children}
    </ModalManagerContext.Provider>
  );
};

export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error('useModalManager must be used within a ModalManagerProvider');
  }
  return context;
};